import { useEffect, useState } from "react";
import { Modal, Button, Spinner, Alert, Badge, Form } from "react-bootstrap";
import useApi from "../hooks/apiService";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

function ItemAttributesModal({
  show,
  onHide,
  item,
  isAttribute = false,
  hideButton,
  disableLocking = false,
}) {
  const { fetchData, deleteData, updateData } = useApi();
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { t } = useTranslation();
  const DATA_TYPE_LABEL = {
    S: t("Texto"),
    N: t("Numérico"),
    B: t("Booleano"),
  };
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const accesObjs = isAttribute ? "record" : "attribute";

  const isBoolean = (item) => item.attribute?.data_type === "B";

  const normalizeBoolean = (value) => {
    if (typeof value === "boolean") return value;
    return value === "true" || value === "1" || value === 1;
  };

  const startEdit = (item) => {
    setEditingId(item.id);

    if (item.attribute?.data_type === "B") {
      setEditValue(normalizeBoolean(item.value));
    } else {
      setEditValue(item.value ?? "");
    }
  };
  const loadAttributeFeaturesMap = async (attributeIds) => {
    if (!attributeIds.length) return {};

    const response = await fetchData("/feature-attributes");

    const map = {};

    (response.data || []).forEach((af) => {
      if (!map[af.attribute.id]) map[af.attribute.id] = [];
      map[af.attribute.id].push(af.feature);
    });

    return map;
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleUpdate = async (attributeValueId) => {
    setUpdatingId(attributeValueId);

    try {
      await updateData(`/attribute-values/${attributeValueId}`, {
        value: String(editValue),
      });

      setAttributes((prev) =>
        prev.map((item) =>
          item.id === attributeValueId ? { ...item, value: editValue } : item,
        ),
      );

      cancelEdit();
    } catch (err) {
      console.error(err);

      alert("Erro ao atualizar valor.");
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    if (!show || !item) return;

    const fetchAttributes = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchData(
          `/attribute-values/${isAttribute ? "attributes" : "records"}/${item.id}`,
        );

        const data = response.data || [];

        const attributeIds = [
          ...new Set(data.map((av) => av.attribute?.id).filter(Boolean)),
        ];

        const featuresMap = await loadAttributeFeaturesMap(attributeIds);

        const filtered = data.filter((av) => {
          const attr = av.attribute;
          const attrId = attr?.id;

          if (!isAttribute && attr?.slug?.startsWith("adm_")) {
            return false;
          }
          if (disableLocking) return true;
          const features = featuresMap[attrId];
          if (!features || features.length === 0) return true;
          const allDisabled = features.every((f) => !f.is_enabled);

          return !allDisabled;
        });

        setAttributes(filtered);
      } catch (err) {
        setError("Erro ao carregar os atributos do item.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [show, item, isAttribute]);

  const handleDelete = async (attributeValueId) => {
    setDeletingId(attributeValueId);

    try {
      await deleteData(`/attribute-values/${attributeValueId}`);

      // Remove visualmente o item
      setAttributes((prev) =>
        prev.filter((item) => item.id !== attributeValueId),
      );
    } catch (err) {
      console.error(err);
      alert("Erro ao desvincular atributo.");
    } finally {
      setDeletingId(null);
      setConfirmingId(null);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      );
    }

    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    if (!attributes.length) {
      return <Alert variant="secondary">{t("no_attributes_found")}</Alert>;
    }

    return (
      <div className="d-flex flex-column gap-3">
        {attributes.map((item) => (
          <div
            key={item.id}
            className="border rounded p-3 shadow-sm bg-light position-relative"
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="d-flex gap-3">
                <h6 className="mb-0 fw-semibold">
                  {item?.[accesObjs]?.name ||
                    (item.record.mapping_group?.name &&
                      item.record.mapping_group?.name +
                        " > " +
                        item.record.variable?.name) ||
                    item.id}
                </h6>
                <Badge bg="info" pill>
                  {DATA_TYPE_LABEL[item.attribute?.data_type]}
                </Badge>
              </div>
              {hideButton && (
                <div className="d-flex align-items-center gap-2">
                  {/* Lixeira / Confirmação */}
                  {confirmingId === item.id ? (
                    <div className="d-flex gap-2 text-nowrap">
                      <Button
                        className="text-nowrap"
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setConfirmingId(null)}
                        disabled={deletingId === item.id}
                      >
                        {t("button_cancel")}
                      </Button>

                      <Button
                        className="text-nowrap"
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                      >
                        {deletingId === item.id ? (
                          <Spinner size="sm" />
                        ) : (
                          "Confirmar"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <i
                      className="bi bi-trash text-danger"
                      role="button"
                      title="Desvincular atributo"
                      onClick={() => setConfirmingId(item.id)}
                      style={{ fontSize: "1.1rem" }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Detalhes */}
            {item[accesObjs]?.details && (
              <div>
                <small className="text-muted">{t("details")}</small>
                <div className="text-secondary">{item[accesObjs]?.details}</div>
              </div>
            )}

            {/* Valor */}
            <div className="mb-2">
              <small className="text-muted">{t("value")}</small>

              {editingId === item.id ? (
                <div className="mt-1">
                  {isBoolean(item) ? (
                    <div className="d-flex align-items-center gap-3">
                      <Form.Check
                        type="switch"
                        id={`switch-${item.id}`}
                        label={editValue ? t("Verdadeiro") : t("Falso")}
                        checked={editValue}
                        onChange={(e) => setEditValue(e.target.checked)}
                      />

                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={cancelEdit}
                        disabled={updatingId === item.id}
                      >
                        {t("button_cancel")}
                      </Button>

                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleUpdate(item.id)}
                        disabled={updatingId === item.id}
                      >
                        {updatingId === item.id ? (
                          <Spinner size="sm" />
                        ) : (
                          t("Salvar")
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex gap-2 align-items-center text-nowrap">
                      <input
                        className="form-control form-control-sm"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      />

                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={cancelEdit}
                        disabled={updatingId === item.id}
                      >
                        {t("button_cancel")}
                      </Button>

                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleUpdate(item.id)}
                        disabled={updatingId === item.id}
                      >
                        {updatingId === item.id ? (
                          <Spinner size="sm" />
                        ) : (
                          t("Salvar")
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2 text-nowrap">
                  {isBoolean(item) ? (
                    <Badge
                      bg={
                        normalizeBoolean(item.value) ? "success" : "secondary"
                      }
                    >
                      {normalizeBoolean(item.value)
                        ? t("Verdadeiro")
                        : t("Falso")}
                    </Badge>
                  ) : (
                    <div className="fw-medium">{item.value || "-"}</div>
                  )}
                  {hideButton && (
                    <i
                      className="bi bi-pencil"
                      role="button"
                      title={t("Editar valor")}
                      onClick={() => startEdit(item)}
                      style={{ fontSize: "0.95rem" }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title>
          {isAttribute ? "Valores do atributo" : t("attributes_of")}{" "}
          {" " +
            (item.name ||
              item.slug ||
              item.mapping_group?.name + " > " + item.variable?.name ||
              item.record.mapping_group?.name +
                " > " +
                item.record.variable?.name ||
              item.id)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>{renderContent()}</Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("button_close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ItemAttributesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  item: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hideButton: PropTypes.bool,
  disableLocking: PropTypes.bool,
};

export default ItemAttributesModal;
